import { useMemo } from 'react';

import { ChakraBox, ChakraFlex, Tab } from '@equip.health/ui';

import {
  getAppointmentTypeDescription,
  getGroupedAppointmentTypes,
  getGroupedCronofyRules,
} from '~/lib/util/calendar.util';
import Loading from '~/pages/loading';
import Label from './Label';

const TOP_LEVEL_ID = 'rule-select-v2';

type RuleSelectMenuButtonType = 'simple' | 'detailed';

interface RuleSelectV2Props {
  appointmentTypes: AppointmentType[];
  buttonType?: RuleSelectMenuButtonType;
  handleRuleSelect: (value: string) => void;
  isDisabled: boolean;
  isLoading: boolean;
  label?: string;
  selectedAppointmentType: AppointmentType;
  variant: 'availability' | 'scheduling';
}

const RuleSelectV2 = ({
  appointmentTypes,
  buttonType = 'detailed',
  handleRuleSelect,
  isDisabled,
  isLoading,
  label,
  selectedAppointmentType,
  variant,
}: RuleSelectV2Props) => {
  const isAvailabilityVariant = variant === 'availability';

  const rules = useMemo(() => {
    let apptTypes = appointmentTypes ?? [];
    if (isAvailabilityVariant) {
      const hadCronofyRules = apptTypes.every(
        (apptType) => !!apptType.cronofyRule,
      );

      apptTypes = hadCronofyRules
        ? getGroupedCronofyRules(apptTypes)
        : getGroupedAppointmentTypes(apptTypes);
    }

    return apptTypes.map((apptType) => {
      const {
        appointmentType,
        cronofyRule,
        cronofyRuleLabel,
        appointmentTypeExtId,
        bufferInMinutes,
        durationInMinutes,
      } = apptType;

      let title = '';
      if (buttonType === 'detailed') {
        title = isAvailabilityVariant
          ? (cronofyRuleLabel ?? cronofyRule ?? appointmentType)
          : `${cronofyRule ?? appointmentType} - ${getAppointmentTypeDescription(
              durationInMinutes,
              bufferInMinutes,
            )}`;
      } else {
        title = `${cronofyRule ?? appointmentType}, ${durationInMinutes} minutes`;
      }

      return {
        title,
        value: appointmentTypeExtId,
      };
    });
  }, [appointmentTypes, buttonType, isAvailabilityVariant]);

  if (isLoading) return <Loading />;
  if (!rules.length) return null;

  return (
    <ChakraBox cursor={isDisabled ? 'not-allowed' : 'default'} width="100%">
      {label && <Label id={TOP_LEVEL_ID}>{label}</Label>}

      <ChakraFlex
        cursor={isDisabled ? 'not-allowed' : 'default'}
        gap="8px"
        id={`${TOP_LEVEL_ID}__tab-list`}
        opacity={isDisabled ? 0.5 : 1}
        pointerEvents={isDisabled ? 'none' : 'auto'}
        wrap="wrap"
      >
        {rules.map(({ value, title }) => {
          const isActive =
            selectedAppointmentType?.appointmentTypeExtId === value;
          return (
            <Tab
              id={`${TOP_LEVEL_ID}__tab__${value}`}
              isActive={isActive}
              key={value}
              onClick={() => {
                if (!isDisabled) handleRuleSelect(value);
              }}
              size="normal"
              title={title}
              variant="bubble"
            />
          );
        })}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default RuleSelectV2;
